import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, Phone, Mail, Calendar, Clock, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import LeadModal from '@/components/LeadModal';

import { API_URL } from '@/config/api';

const FollowUpsPage = () => {
  const { selectedCampaign } = useAuth();
  const [followUps, setFollowUps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchFollowUps = useCallback(async () => {
    if (!selectedCampaign) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API_URL}/api/leads/followups?campaign_id=${selectedCampaign.campaign_id}`,
        { credentials: 'include', headers }
      );

      if (response.ok) {
        setFollowUps(await response.json());
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      toast.error('Failed to load follow-ups');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    fetchFollowUps();
  }, [fetchFollowUps]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleLeadUpdate = () => {
    fetchFollowUps();
    setShowLeadModal(false);
    setSelectedLead(null);
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const callbackDate = new Date(dateStr);
    callbackDate.setHours(0, 0, 0, 0);
    
    const diffDays = Math.floor((callbackDate - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  if (!selectedCampaign) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">No Campaign Selected</h2>
          <p className="text-slate-500 mt-2">Please select a campaign from the sidebar</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#00B4D8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Group follow-ups by urgency
  const overdue = followUps.filter(l => isOverdue(l.callback_date));
  const today = followUps.filter(l => {
    if (!l.callback_date) return false;
    const todayStr = new Date().toISOString().split('T')[0];
    return l.callback_date === todayStr;
  });
  const upcoming = followUps.filter(l => {
    if (!l.callback_date) return false;
    const todayStr = new Date().toISOString().split('T')[0];
    return l.callback_date > todayStr;
  });

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="followups-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Follow-ups
        </h1>
        <p className="text-sm sm:text-base text-slate-500 mt-1 truncate">Callbacks - {selectedCampaign.name}</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className={`card-hover ${overdue.length > 0 ? 'border-red-200 bg-red-50/30' : ''}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <div className={`p-2 rounded-lg ${overdue.length > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                <AlertCircle className={`w-4 h-4 sm:w-5 sm:h-5 ${overdue.length > 0 ? 'text-red-600' : 'text-slate-500'}`} />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{overdue.length}</p>
                <p className="text-xs sm:text-sm text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={`card-hover ${today.length > 0 ? 'border-amber-200 bg-amber-50/30' : ''}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <div className={`p-2 rounded-lg ${today.length > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <Clock className={`w-4 h-4 sm:w-5 sm:h-5 ${today.length > 0 ? 'text-amber-600' : 'text-slate-500'}`} />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{today.length}</p>
                <p className="text-xs sm:text-sm text-slate-500">Today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <div className="p-2 rounded-lg bg-sky-100">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-[#00B4D8]" />
              </div>
              <div className="text-center sm:text-left">
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{upcoming.length}</p>
                <p className="text-xs sm:text-sm text-slate-500">Upcoming</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Follow-up Lists */}
      {followUps.length === 0 ? (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <Calendar className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-slate-700">No Follow-ups Scheduled</h3>
            <p className="text-sm text-slate-500 mt-2">All caught up! No leads need callbacks right now.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4 sm:space-y-6">
          {/* Overdue */}
          {overdue.length > 0 && (
            <Card className="border-red-200" data-testid="overdue-section">
              <CardHeader className="pb-3 bg-red-50">
                <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Overdue ({overdue.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {overdue.map((lead) => (
                    <FollowUpItem 
                      key={lead.lead_id} 
                      lead={lead} 
                      onClick={() => handleLeadClick(lead)}
                      formatDate={formatDate}
                      isOverdue={true}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Today */}
          {today.length > 0 && (
            <Card className="border-amber-200" data-testid="today-section">
              <CardHeader className="pb-3 bg-amber-50">
                <CardTitle className="text-lg text-amber-700 flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Due Today ({today.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {today.map((lead) => (
                    <FollowUpItem 
                      key={lead.lead_id} 
                      lead={lead} 
                      onClick={() => handleLeadClick(lead)}
                      formatDate={formatDate}
                      isToday={true}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming */}
          {upcoming.length > 0 && (
            <Card data-testid="upcoming-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00B4D8]" />
                  Upcoming ({upcoming.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-100">
                  {upcoming.map((lead) => (
                    <FollowUpItem 
                      key={lead.lead_id} 
                      lead={lead} 
                      onClick={() => handleLeadClick(lead)}
                      formatDate={formatDate}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Lead Modal */}
      <LeadModal 
        isOpen={showLeadModal}
        onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
        lead={selectedLead}
        campaignId={selectedCampaign?.campaign_id}
        onUpdate={handleLeadUpdate}
      />
    </div>
  );
};

const FollowUpItem = ({ lead, onClick, formatDate, isOverdue, isToday }) => {
  return (
    <div 
      className="p-4 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4"
      onClick={onClick}
      data-testid={`followup-item-${lead.lead_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-semibold text-slate-900 truncate">{lead.name}</h4>
          <Badge className={`${isOverdue ? 'status-lost' : isToday ? 'status-callback' : 'status-new'} text-xs`}>
            {formatDate(lead.callback_date)}
          </Badge>
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          {lead.phone && (
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {lead.phone}
            </span>
          )}
          {lead.email && (
            <span className="flex items-center gap-1 truncate">
              <Mail className="w-3 h-3" />
              {lead.email}
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {lead.phone && (
          <Button 
            size="sm" 
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`tel:${lead.phone}`);
            }}
          >
            <Phone className="w-4 h-4 mr-1" />
            Call
          </Button>
        )}
        <ChevronRight className="w-5 h-5 text-slate-400" />
      </div>
    </div>
  );
};

export default FollowUpsPage;
